/* eslint-disable no-console */
import { SUBSCRIBE_OHLC_CANDEL, UNSUBSCRIBE_OHLC_CANDEL } from 'actionTypes/socket';
import { CHARTS_API } from 'constants/api';
import { constructQueryString } from 'helpers/utils';
import store from 'store';
import { setTradingViewSubscriptionData } from 'variableStore/actions/tradingview';

import { LibrarySymbolInfo, PeriodParams, Bar } from './charting_library';
import { calculateFromTime, calculateToTime, getChartCacheTTL } from './helper';

const requester = async (url: string) => {
  try {
    const response = await fetch(url);
    const responseTest = await response.text();
    return JSON.parse(responseTest).result;
  } catch (error) {
    console.error('[requester]: Method call error', error);
    return null;
  }
};
/**
 * ref: https://www.tradingview.com/charting-library-docs/latest/connecting_data/Datafeed-API
 */
export type PeriodParamsWithOptionalCountback = Omit<PeriodParams, 'countBack'> & {
  countBack?: number;
};

const getSupportedResolution = data => {
  if (data.supported_resolutions) {
    return data.supported_resolutions;
  }
  return ['1', '5', '15', '30', '60', '10', '45', '90', '180', '720', 'D', 'W', 'M'];
};

const datafeed = () => {
  const lastBarsData = new Map();
  return {
    onReady: async (callback: any) => {
      console.log('DEBUG [onReady]: Method call');
      const configurationData = await requester(CHARTS_API.CONFIG);
      setTimeout(
        () =>
          callback({
            ...configurationData,
            supported_resolutions: getSupportedResolution(configurationData),
          }),
        0
      );
    },
    searchSymbols: async (
      userInput: string,
      exchange: string,
      symbolType: string,
      onResultReadyCallback: any
    ) => {
      const queryString = {
        limit: 30,
        query: userInput,
        exchange,
        symbolType,
      };
      const api = constructQueryString(CHARTS_API.COMPARE_SYMBOL, queryString);
      const data = await requester(api);
      console.log('DEBUG [searchSymbols]: Method call', {
        userInput,
        exchange,
        symbolType,
        data,
      });
      setTimeout(() => {
        onResultReadyCallback(data);
      }, 0);
    },
    resolveSymbol: async (
      symbolName: string,
      onSymbolResolvedCallback: any,
      onResolveErrorCallback: any
    ) => {
      try {
        const symbol = symbolName.includes('Delta:')
          ? symbolName.split(':')[1]
          : symbolName;
        console.log('DEBUG [resolveSymbol]: Method call', symbolName);
        const data = await requester(`${CHARTS_API.SEARCH_SYMBOL}?symbol=${symbol}`);
        if (data) {
          const symbolInfo: LibrarySymbolInfo = {
            ...data,
            name: data.name,
            base_name: `Delta:${data.name}`,
            full_name: data.name,
            listed_exchange: 'Delta',
            exchange: 'Delta',
            currency_code: data.currency_code ?? data['currency-code'],
            original_currency_code: data.currency_code ?? data['currency-code'],
            // unit_id: response.unit_id ?? response['unit-id'],
            // original_unit_id: response.original_unit_id ?? response['original-unit-id'],
            // unit_conversion_types:
            // response.unit_conversion_types ?? response['unit-conversion-types'],
            has_intraday: data.has_intraday ?? data['has-intraday'] ?? true,
            visible_plots_set: data.visible_plots_set ?? data['visible-plots-set'],
            minmov: data.minmovement ?? data.minmov ?? 1,
            minmove2: data.minmovement2 ?? data.minmove2,
            session: data.session ?? data['session-regular'],
            session_holidays: data.session_holidays ?? data['session-holidays'],
            supported_resolutions: getSupportedResolution(data),
            has_daily: data.has_daily ?? data['has-daily'] ?? true,
            intraday_multipliers: data.intraday_multipliers ??
              data['intraday-multipliers'] ?? ['1', '5', '15', '30', '60'],
            has_weekly_and_monthly:
              data.has_weekly_and_monthly ?? data['has-weekly-and-monthly'],
            has_empty_bars: data.has_empty_bars ?? data['has-empty-bars'],
            volume_precision: data.volume_precision ?? data['volume-precision'],
            format: data.format ?? 'price',
          };
          setTimeout(() => {
            onSymbolResolvedCallback(symbolInfo);
          }, 0);
        } else {
          setTimeout(() => {
            onResolveErrorCallback('Not found');
          }, 0);
        }
      } catch (error) {
        console.error('[resolveSymbol]: Method call error', error);
        onResolveErrorCallback('Not found');
      }
    },
    getBars: async (
      symbolInfo: LibrarySymbolInfo,
      resolution: string,
      periodParams: PeriodParamsWithOptionalCountback,
      onHistoryCallback: any,
      onErrorCallback: any
    ) => {
      try {
        const apiResolution = resolution === '1D' ? 'D' : resolution;
        const chartCacheTTL = getChartCacheTTL(apiResolution);
        const toTime = calculateToTime({
          to: periodParams.to,
          resolution: apiResolution,
        });
        const fromTime = calculateFromTime({
          countBack: periodParams.countBack,
          to: periodParams.to,
          resolution: apiResolution,
        });
        const data = await requester(
          `${CHARTS_API.HISTORY_SYMBOL}?symbol=${symbolInfo.name}&resolution=${apiResolution}&from=${fromTime}&to=${toTime}&cache_ttl=${chartCacheTTL}`
        );
        if (data && data.t.length > 0) {
          let bars: Bar[] = data.t
            .map((time: number, index: number) => ({
              time: time * 1000, // Convert to milliseconds
              close: parseFloat(data.c[index]),
              open: parseFloat(data.o[index]),
              high: parseFloat(data.h[index]),
              low: parseFloat(data.l[index]),
              volume: parseFloat(data.v[index]),
            }))
            .filter(bar => bar.time >= fromTime * 1000 && bar.time < toTime * 1000);

          if (periodParams.firstDataRequest) {
            lastBarsData.set(symbolInfo.name, { ...bars[bars.length - 1] });
          }

          if (periodParams.countBack && bars.length < periodParams.countBack) {
            const additionalBarsNeeded = periodParams.countBack - bars.length;
            const earliestBarTime =
              bars.length > 0 ? bars[0].time / 1000 : periodParams.from; // Convert back to seconds
            const additionalBars = await requester(
              `${CHARTS_API.HISTORY_SYMBOL}?symbol=${
                symbolInfo.name
              }&resolution=${apiResolution}&from=${
                earliestBarTime - additionalBarsNeeded * 60 * 60 * 24
              }&to=${earliestBarTime}`
            );
            if (additionalBars && additionalBars.t.length > 0) {
              const additionalBarsArray = additionalBars.t.map(
                (time: number, index: number) => ({
                  time: time * 1000, // Convert to milliseconds
                  close: parseFloat(additionalBars.c[index]),
                  open: parseFloat(additionalBars.o[index]),
                  high: parseFloat(additionalBars.h[index]),
                  low: parseFloat(additionalBars.l[index]),
                  volume: parseFloat(additionalBars.v[index]),
                })
              );
              bars = [...additionalBarsArray, ...bars];
            }
          }

          if (bars.length === 0) {
            setTimeout(() => {
              onHistoryCallback([], { noData: true });
            }, 0);
          } else {
            setTimeout(() => {
              onHistoryCallback(bars, { noData: false });
            }, 0);
          }
        } else {
          onHistoryCallback([], { noData: true });
        }
      } catch (error) {
        console.error('[getBars]: Method call error', error);
        onErrorCallback(error);
      }
    },
    subscribeBars: (
      symbolInfo: LibrarySymbolInfo,
      resolution: string,
      onRealtimeCallback: any,
      subscribeUID: string,
      onResetCacheNeededCallback: any
    ) => {
      console.log(
        'DEBUG [subscribeBars]: Method call',
        symbolInfo,
        resolution,
        lastBarsData,
        subscribeUID
      );
      // clearTradingViewSubscriptionData();
      const symbol = symbolInfo.name;

      const newSub = {
        symbol,
        resolution: resolution === '1D' ? 'D' : resolution,
        symbolInfo,
        lastBar: lastBarsData.get(symbol),
        onRealtimeCallback,
        subscribeUID,
        onResetCacheNeededCallback,
      };

      setTradingViewSubscriptionData(newSub);
      store.dispatch({
        type: SUBSCRIBE_OHLC_CANDEL,
        payload: {
          symbol,
          resolution: resolution === '1D' ? 'D' : resolution,
          subscribeUID,
        },
      });
    },
    unsubscribeBars: (subscribeUID: string) => {
      store.dispatch({
        type: UNSUBSCRIBE_OHLC_CANDEL,
        payload: {
          subscribeUID,
        },
      });
    },
  };
};

export default datafeed;
